import React, { useEffect, useState } from 'react';
import './NavBar.style.scss';
import { useNavBar } from '../../../Contexts/navbar.items.context.jsx';
import { useAllThingsLists } from '../../../Contexts/allThingsList.context.jsx';
import NavPill from '../../NavItems/NavPill/NavPill.component.jsx';
import FilterBox from '../../Universals/filterBox/filterBox.component.jsx';
import { usePlayer } from '../../../Contexts/playerData.context.jsx';
import { useBoxContext } from '../../../Contexts/boxData.context.jsx';
import { getUserFromToken, trackUsageEvent } from '../../../utils/functions.ts';

const QUICK_NOTES_ENDPOINT = "https://www.fbxchange.com/ReactPHP/quickNotes.php";
const TASKS_ENDPOINT = "https://www.fbxchange.com/ReactPHP/tasks.php";
const QUICK_NOTES_REFRESH_EVENT = "FBX_QUICK_NOTES_CHANGED";

const hasAdminAccess = (user) => {
     const securityLevel = String(user?.userContactLevel ?? user?.security ?? user?.ADMIN ?? user?.admin ?? '').trim();
     return securityLevel === '1' || user?.ADMIN === true || user?.admin === true;
};

const canUseTaskManager = (user) => {
     return String(user?.assignTask || '').trim().toLowerCase() === 'yes';
};

const getScoutName = (user) => (
     user?.displayName ||
     user?.scoutName ||
     user?.name ||
     `${user?.firstName || ""} ${user?.lastName || ""}`.trim() ||
     String(user?.scout || "")
);

const normalizeQuickNoteCount = (player) => {
     const count = Number(player?.quickNoteCount ?? player?.QUICKNOTECOUNT ?? 0);
     if (Number.isFinite(count) && count > 0) return count;

     const hasQuickNote = String(player?.hasQuickNote ?? player?.HASQUICKNOTE ?? '').trim().toUpperCase();
     return hasQuickNote === 'Y' ? 1 : 0;
};

const NavigationBar = () => {
     const { searchedListName, nipCount, usaCount, gloCount, vCount, activeSideButton, updateActiveSideButton } = useNavBar();
     const { filterBoxValue, isListPillFiltered, isListFiltered, setIsListPillFiltered } = useAllThingsLists();
     const { playerData, playerDataLength, playerID, activePlayer, setActiveFilter, activeFilter } = usePlayer();
     const { setNoteBoxOpen } = useBoxContext();

     const mainFields = ["Reset", "Last Name", "First Name", "Gen Position", "School", "CFL Team", "NFL Team", "FCY", "UFL24 Team"];
     const [hasAdmin, setHasAdmin] = useState(false);
     const [canUseTasks, setCanUseTasks] = useState(false);
     const [quickNoteCount, setQuickNoteCount] = useState(0);
     // eslint-disable-next-line
     const noteFields = [...mainFields, "Note"];
     // eslint-disable-next-line
     const scoutFields = [...mainFields, "Grade (>=)", "Calc Grade (>=)"];
     const [showFields, setShowFields] = useState({});
     const hasPlayers = Array.isArray(playerData) ? playerData.length > 0 : playerDataLength > 0;
     const hasActivePlayer = String(playerID || activePlayer?.FBXID || '').trim() !== '';
     const activeFBXID = String(playerID || activePlayer?.FBXID || '').trim();
     const activeQuickNoteCount = normalizeQuickNoteCount(activePlayer);

     useEffect(() => {
          let isMounted = true;

          const loadUserDisplaySettings = async () => {
               const user = await getUserFromToken();
               if (!isMounted) return;

               const userHasAdminAccess = hasAdminAccess(user);
               setHasAdmin(userHasAdminAccess);
               const canAssignTasks = canUseTaskManager(user);
               setCanUseTasks(canAssignTasks);
               setShowFields(user?.showFields || {});

               if (!user?.team || !user?.scout) return;

               try {
                    const response = await fetch(TASKS_ENDPOINT, {
                         method: "POST",
                         headers: { "Content-Type": "application/json" },
                         body: JSON.stringify({
                              scriptName: "getTasks",
                              team: user.team,
                              scoutId: user.scout,
                              scoutName: getScoutName(user),
                              userContactLevel: user.userContactLevel ?? user.security ?? "",
                         }),
                    });
                    const data = await response.json();
                    if (!isMounted || !data?.success) return;
                    setCanUseTasks(canAssignTasks || (Array.isArray(data.tasks) && data.tasks.length > 0));
               } catch {
                    if (isMounted) setCanUseTasks(canAssignTasks);
               }
          };

          loadUserDisplaySettings();

          const handleFocus = () => loadUserDisplaySettings();
          window.addEventListener('focus', handleFocus);

          return () => {
               isMounted = false;
               window.removeEventListener('focus', handleFocus);
          };
     }, []);

     const isShowFieldNo = (field) => String(showFields?.[field] || '').toLowerCase() === 'no';

     const NavBarPushed = (boxName) => {
          const labels = {
               DraftBoard: "White Board Opened",
               Tasks: "Tasks Opened",
               TomBox: "Add/Edit Opened",
               Overview: "Overview Opened",
               Export: "Export Opened",
               hdbox: "Hard Data Opened",
               transactions: "Transactions Opened",
               reports1: "Reports Opened",
               notes: "Notes Opened",
               Files: "Files Opened",
          };
          trackUsageEvent(labels[boxName] || `${boxName} Opened`, {
               source: "top-navigation",
               metadata: { buttonId: boxName },
          });
          setNoteBoxOpen(false);
          updateActiveSideButton(prev =>
               prev === boxName ? '' : boxName
          );
     };

     const openQuickNotes = () => {
          trackUsageEvent("QN Opened", {
               source: "top-navigation",
               metadata: { buttonId: "QN", FBXID: activeFBXID },
          });
          setNoteBoxOpen(false);
          updateActiveSideButton('QN');
          window.dispatchEvent(new CustomEvent("FBX_QUICK_NOTES_CHANGED", {
               detail: { FBXID: activeFBXID },
          }));
     };


     const pillClicked = (pillValue) => {
          if (!isListFiltered) {
               if (activeFilter === pillValue) {
                    setActiveFilter('');
                    setIsListPillFiltered(false);
               } else {
                    setActiveFilter(pillValue);
                    setIsListPillFiltered(true);
               }
          }

     }

     useEffect(() => {
          setQuickNoteCount(activeQuickNoteCount);
     }, [activeFBXID, activeQuickNoteCount]);

     useEffect(() => {
          if (!activeFBXID) {
	               setQuickNoteCount(0);
	               return undefined;
	          }

          let isCurrent = true;

          const loadQuickNoteCount = async () => {
               const tokenUser = await getUserFromToken();
               if (!isCurrent || !tokenUser?.team) return;

               try {
                    const response = await fetch(QUICK_NOTES_ENDPOINT, {
                         method: "POST",
                         headers: { "Content-Type": "application/json" },
                         body: JSON.stringify({
                              scriptName: "getQuickNoteCount",
                              team: tokenUser.team,
                              FBXID: activeFBXID,
                         }),
                    });
                    const data = await response.json();
                    if (isCurrent && data?.success) {
                         setQuickNoteCount(Number(data.quickNoteCount ?? 0) || 0);
                    }
               } catch {
                    if (isCurrent) setQuickNoteCount(activeQuickNoteCount);
               }
          };

          const handleQuickNotesChanged = (event) => {
               const changedFBXID = String(event?.detail?.FBXID ?? '').trim();
               if (changedFBXID && changedFBXID !== activeFBXID) return;

               if (event?.detail?.quickNoteCount !== undefined) {
                    setQuickNoteCount(Number(event.detail.quickNoteCount ?? 0) || 0);
                    return;
               }

               loadQuickNoteCount();
          };

          loadQuickNoteCount();
          window.addEventListener(QUICK_NOTES_REFRESH_EVENT, handleQuickNotesChanged);

          return () => {
               isCurrent = false;
               window.removeEventListener(QUICK_NOTES_REFRESH_EVENT, handleQuickNotesChanged);
          };
	     }, [activeFBXID, activeQuickNoteCount]);

     return (
          <div className='nav-bar'>
               <div className='left'>
                    <>
                         <FilterBox
                              // key={filterResetKey}
                              displayValue={filterBoxValue}
                              chosenFields={mainFields}
                              readOnly={isListPillFiltered}  // 🔥 Ensure it's a boolean
                         />

                         <div className="ms-2 me-2 text-light fs-8" style={{ minWidth: "75px" }}>{playerDataLength} Records</div>

                         <div className="d-flex gap-1" style={{ marginLeft: "5px" }}>

                              {/* nav pills are deal with in pageContent */}

                              <NavPill
                                   theTitle="Nationals"
                                   isPillFiltered={activeFilter === 'N'}
                                   theValue={nipCount}
                                   buttonColor={isListFiltered ? "grey" : activeFilter === 'N' ? "orange" : "var(--danger)"}
                                   onClick={() => pillClicked('N')}
                                   isDisabled={isListFiltered}
                              />
                              <NavPill
                                   theTitle="American"
                                   isPillFiltered={activeFilter === 'U'}
                                   theValue={usaCount}
                                   buttonColor={isListFiltered ? "grey" : activeFilter === 'U' ? "orange" : "var(--primary)"}
                                   onClick={() => pillClicked('U')}
                                   isDisabled={isListFiltered}
                              />
                              <NavPill
                                   theTitle="Globals"
                                   isPillFiltered={activeFilter === 'G'}
                                   theValue={gloCount}
                                   buttonColor={isListFiltered ? "grey" : activeFilter === 'G' ? "orange" : "#bae97480"}
                                   onClick={() => pillClicked('G')}
                                   isDisabled={isListFiltered}
                              />
                              <NavPill
                                   theTitle="Nationalized Americans"
                                   isPillFiltered={activeFilter === 'V'}
                                   theValue={vCount}
                                   buttonColor={isListFiltered ? "grey" : activeFilter === 'V' ? "orange" : "var(--brown)"}
                                   onClick={() => pillClicked('V')}
                                   isDisabled={isListFiltered}
                              />
                         </div>

                         <div className="ms-2 text-light fs-8">{searchedListName}</div>
                    </>
	               </div>
	               <div className="right">
                         <div
                              className={`btn-long ${activeSideButton === 'DraftBoard' ? 'btn-warning' : 'bg-light text-dark'} btn-hover-warning`}
                              onClick={() => NavBarPushed('DraftBoard')}
                         >
                              White Board
                         </div>
                         {canUseTasks && (
                              <div
                                   className={`btn-long ${activeSideButton === 'Tasks' ? 'btn-warning' : 'bg-light text-dark'} btn-hover-warning`}
                                   onClick={() => NavBarPushed('Tasks')}
                              >
                                   Tasks
                              </div>
                         )}
	                    {!hasPlayers ? (
	                         <div className="nav-empty-state">No players selected</div>
	                    ) : (
	                         <>
                              {hasAdmin && (
                                   <div
                                        className={`btn-long ${activeSideButton === 'TomBox' ? 'btn-warning' : 'bg-light text-dark'} btn-hover-warning`}
                                        onClick={() => NavBarPushed('TomBox')}
                                   >
                                        Add/Edit
                                   </div>
                              )}
	                              {!isShowFieldNo('showOprah') && (
                                   <div
                                        className={`btn-long ${activeSideButton === 'Overview' ? 'btn-warning' : 'bg-light text-dark'} btn-hover-warning`}
                                        onClick={() => NavBarPushed('Overview')}
                                   >
                                        Overview
                                   </div>
                              )}
                              {!isShowFieldNo('showEXPORT') && (
                                   <div className="d-flex justify-content-end align-items-center">
                                        <div
                                             className={`btn-long ${activeSideButton === 'Export' ? 'btn-warning' : 'bg-light text-dark'} btn-hover-warning`}
                                             onClick={() => NavBarPushed('Export')}
                                        >
                                             Export
                                        </div>
                                   </div>
                              )}
                              {!isShowFieldNo('showHWS') && (
                                   <div
                                        className={`btn-long ${activeSideButton === 'hdbox' ? 'btn-warning' : 'bg-light text-dark'} btn-hover-warning`}
                                        onClick={() => NavBarPushed('hdbox')}
                                   >
                                        Hard Data
                                   </div>
                              )}
                              {!isShowFieldNo('showTRANS') && (
                                   <div
                                        className={`btn-long ${activeSideButton === 'transactions' ? 'btn-warning' : 'bg-light text-dark'} btn-hover-warning`}
                                        onClick={() => NavBarPushed('transactions')}
                                   >
                                        Transactions
                                   </div>
                              )}
                              {!isShowFieldNo('showREPORT') && (
                                   <div
                                        className={`btn-long ${activeSideButton === 'reports1' ? 'btn-warning' : 'bg-light text-dark'} btn-hover-warning`}
                                        onClick={() => NavBarPushed('reports1')}
                                   >
                                        Reports
                                   </div>
                              )}
                              {!isShowFieldNo('showNOTE') && (
                                   <div
                                        className={`btn-long ${activeSideButton === 'notes' ? 'btn-warning' : 'bg-light text-dark'} btn-hover-warning`}
                                        onClick={() => NavBarPushed('notes')}
                                   >
                                        Notes
                                   </div>
                              )}
                              {!isShowFieldNo('showREPORT') && (
                                   <div
                                        className={`btn-long ${activeSideButton === 'Files' ? 'btn-warning' : 'bg-light text-dark'} btn-hover-warning`}
                                        onClick={() => NavBarPushed('Files')}
                                   >
                                        Files
                                   </div>
                              )}
	                              {hasActivePlayer && (
	                                   <div
	                                        className={`btn-long qn-nav-button ${activeSideButton === 'QN' ? 'qn-nav-button-active' : ''}`}
                                        onClick={openQuickNotes}
                                        title="Open quick notes"
                                   >
                                        QN [{quickNoteCount}]
                                   </div>
                              )}
                         </>
                    )}
               </div>

          </div>
     );
};

export default NavigationBar;
